import React, { useEffect, useMemo, useState, type FormEvent, type JSX } from "react";
import { useSearchParams } from "react-router-dom";
import type { AdminDashboardDependencies } from "../../app/composition/create-admin-dashboard-dependencies";
import type { AdminUserLookupInput } from "../../contracts/admin-read-models";
import { parseUserLookupParams } from "../../contracts/admin-route-params";
import {
  formatAdminDashboardSummary,
  pickAdminDashboardLocaleText,
} from "../../shared/locale/admin-dashboard-copy";
import { AdminLocalizedLink } from "../../shared/locale/admin-dashboard-localized-link";
import { withAdminDashboardLocaleSearchParams } from "../../shared/locale/admin-dashboard-locale-routing";
import { useAdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale-context";
import { AdminDetailPanel } from "../../shared/ui/admin-detail-panel";
import { AdminEmptyState } from "../../shared/ui/admin-empty-state";
import { AdminErrorState } from "../../shared/ui/admin-error-state";
import { AdminFilterBar } from "../../shared/ui/admin-filter-bar";
import { AdminOperatorExplanationBlock } from "../../shared/ui/admin-operator-explanation-block";
import {
  AdminStatusBadge,
  toAdminFreshnessBadgeValue,
  toAdminRiskBadgeValue,
} from "../../shared/ui/admin-status-badge";
import {
  appendUserMediaWorkspaceLookupContext,
  readUserMediaWorkspaceFocus,
} from "./media-workspace-focus";
import {
  appendUserIncidentWorkspaceFocus,
  readUserIncidentWorkspaceFocus,
} from "./incident-workspace-focus";
import {
  appendUserSameDeviceWorkspaceContext,
  readUserSameDeviceWorkspaceContext,
} from "./same-device-workspace-context";
import { useUserOperationsScreenModel } from "./use-user-operations-screen-model";
import { useUserSummaryListScreenModel } from "./use-user-summary-list-screen-model";

export type UserOperationsScreenProps = {
  dependencies: AdminDashboardDependencies;
};

type UserLookupDraft = {
  internalUserId: string;
  userIdHash: string;
  requestId: string;
  correlationId: string;
};

type UserSummaryListLikeState =
  | {
      status: "ready" | "partial" | "stale";
      viewModel: {
        items: Array<{
          internalUserId: string;
        }>;
      };
    }
  | {
      status: "idle" | "loading" | "empty" | "permission_denied" | "error";
    };

const cardStyle = {
  padding: "20px",
  borderRadius: "22px",
  background: "rgba(255, 255, 255, 0.82)",
  border: "1px solid rgba(24, 33, 43, 0.08)",
  boxShadow: "0 18px 36px rgba(15, 23, 42, 0.08)",
} as const;

function baseControlStyle() {
  return {
    width: "100%",
    minHeight: "40px",
    padding: "10px 12px",
    borderRadius: "14px",
    border: "1px solid rgba(148, 163, 184, 0.45)",
    background: "#fff",
    color: "#18212b",
    fontSize: "0.92rem",
  } as const;
}

function createTriageCardStyle(
  tone: "default" | "attention" | "warning" | "critical",
) {
  if (tone === "critical") {
    return {
      background: "rgba(254, 242, 242, 0.95)",
      border: "1px solid rgba(239, 68, 68, 0.22)",
    } as const;
  }

  if (tone === "warning") {
    return {
      background: "rgba(255, 247, 237, 0.95)",
      border: "1px solid rgba(249, 115, 22, 0.22)",
    } as const;
  }

  if (tone === "attention") {
    return {
      background: "rgba(254, 249, 195, 0.55)",
      border: "1px solid rgba(234, 179, 8, 0.22)",
    } as const;
  }

  return {
    background: "rgba(248, 250, 252, 0.96)",
    border: "1px solid rgba(148, 163, 184, 0.18)",
  } as const;
}

function createLookupSearchParams(formData: FormData, locale: "ko" | "en"): URLSearchParams {
  const params = new URLSearchParams();

  for (const key of ["internalUserId", "userIdHash", "requestId", "correlationId"]) {
    const rawValue = formData.get(key);
    const value = typeof rawValue === "string" ? rawValue.trim() : "";

    if (value.length > 0) {
      params.set(key, value);
    }
  }

  return withAdminDashboardLocaleSearchParams(params, locale);
}

function readLookupDraft(params: URLSearchParams): UserLookupDraft {
  return {
    internalUserId: params.get("internalUserId") ?? "",
    userIdHash: params.get("userIdHash") ?? "",
    requestId: params.get("requestId") ?? "",
    correlationId: params.get("correlationId") ?? "",
  };
}

function toLookupDraft(input: AdminUserLookupInput): UserLookupDraft {
  return {
    internalUserId: input.internalUserId ?? "",
    userIdHash: input.userIdHash ?? "",
    requestId: input.requestId ?? "",
    correlationId: input.correlationId ?? "",
  };
}

function formatLookupSummary(
  params: URLSearchParams,
  locale: "ko" | "en",
): string | undefined {
  return formatAdminDashboardSummary(locale, [
    { key: "internalUserId", value: params.get("internalUserId") },
    { key: "userIdHash", value: params.get("userIdHash") },
    { key: "request", value: params.get("requestId") },
    { key: "correlation", value: params.get("correlationId") },
  ]);
}

function hasLookupValue(lookup: UserLookupDraft): boolean {
  return Boolean(
    lookup.internalUserId ||
      lookup.userIdHash ||
      lookup.requestId ||
      lookup.correlationId,
  );
}

export function resolveUserOperationsActiveLookup(
  lookup: UserLookupDraft,
  listState: UserSummaryListLikeState,
  selectedBaselineInternalUserId?: string,
): UserLookupDraft | undefined {
  if (hasLookupValue(lookup)) {
    return lookup;
  }

  if (
    listState.status !== "ready" &&
    listState.status !== "partial" &&
    listState.status !== "stale"
  ) {
    return undefined;
  }

  const matchedBaselineItem = selectedBaselineInternalUserId
    ? listState.viewModel.items.find((item) => item.internalUserId === selectedBaselineInternalUserId)
    : undefined;
  const baselineItem = matchedBaselineItem ?? listState.viewModel.items[0];

  if (!baselineItem) {
    return undefined;
  }

  return {
    internalUserId: baselineItem.internalUserId,
    userIdHash: "",
    requestId: "",
    correlationId: "",
  };
}

function hasResolvedUserSummaryList(
  state: UserSummaryListLikeState,
): state is Extract<UserSummaryListLikeState, { status: "ready" | "partial" | "stale" }> {
  return state.status === "ready" || state.status === "partial" || state.status === "stale";
}

export function buildDetailRouteWithMediaSelection(
  detailRoute: string,
  params: URLSearchParams,
  locale: "ko" | "en",
): string {
  const focus = readUserMediaWorkspaceFocus(params);
  const incidentFocus = readUserIncidentWorkspaceFocus(params);
  const sameDeviceContext = readUserSameDeviceWorkspaceContext(params);
  const requestId = params.get("requestId")?.trim();
  const correlationId = params.get("correlationId")?.trim();

  const contextualized = appendUserSameDeviceWorkspaceContext(
    appendUserIncidentWorkspaceFocus(
      appendUserMediaWorkspaceLookupContext(
        new URLSearchParams(),
        {
          requestId,
          correlationId,
        },
        focus,
      ),
      incidentFocus ?? {},
    ),
    sameDeviceContext ?? {},
  );
  const localized = withAdminDashboardLocaleSearchParams(contextualized, locale);
  const suffix = localized.toString();
  return suffix.length > 0 ? `${detailRoute}?${suffix}` : detailRoute;
}

export function UserOperationsScreen(props: UserOperationsScreenProps): JSX.Element {
  const { locale } = useAdminDashboardLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();
  const [lookupDraft, setLookupDraft] = useState<UserLookupDraft>(() => readLookupDraft(searchParams));
  const [selectedBaselineInternalUserId, setSelectedBaselineInternalUserId] = useState<string | undefined>(
    undefined,
  );
  useEffect(() => {
    setLookupDraft(readLookupDraft(searchParams));
  }, [searchParams, searchParamsKey]);
  const committedLookup = useMemo(
    () => toLookupDraft(parseUserLookupParams(searchParams, {})),
    [searchParams, searchParamsKey],
  );
  const hasExplicitLookup = hasLookupValue(committedLookup);
  const lookupSummary = formatLookupSummary(searchParams, locale);
  const listState = useUserSummaryListScreenModel(
    useMemo(
      () => ({
        ...props.dependencies,
        locale,
      }),
      [props.dependencies, locale],
    ),
  );
  useEffect(() => {
    if (hasExplicitLookup) {
      return;
    }

    if (!hasResolvedUserSummaryList(listState)) {
      setSelectedBaselineInternalUserId(undefined);
      return;
    }

    const firstItemId = listState.viewModel.items[0]?.internalUserId;
    setSelectedBaselineInternalUserId((current) => {
      if (!firstItemId) {
        return undefined;
      }

      const matched = current
        ? listState.viewModel.items.some((item) => item.internalUserId === current)
        : false;
      return matched ? current : firstItemId;
    });
  }, [hasExplicitLookup, listState]);
  const activeLookup = useMemo(
    () =>
      resolveUserOperationsActiveLookup(
        committedLookup,
        listState,
        selectedBaselineInternalUserId,
      ),
    [committedLookup, listState, selectedBaselineInternalUserId],
  );
  const hasActiveLookup = activeLookup !== undefined;
  const isBaselineDetailMode = !hasExplicitLookup && hasActiveLookup;
  const screenModelDependencies = useMemo(
    () => ({
      ...props.dependencies,
      locale,
      lookup: activeLookup,
    }),
    [props.dependencies, locale, activeLookup],
  );
  const state = useUserOperationsScreenModel(screenModelDependencies);
  const selectedSummaryInternalUserId =
    state.status === "ready" || state.status === "partial" || state.status === "stale"
      ? state.viewModel.internalUserId
      : undefined;
  const detailRouteWithMediaSelection = useMemo(
    () =>
      state.status === "ready" || state.status === "partial" || state.status === "stale"
        ? buildDetailRouteWithMediaSelection(state.viewModel.detailRoute, searchParams, locale)
        : undefined,
    [locale, searchParams, state],
  );
  const copy = {
    section: pickAdminDashboardLocaleText(locale, { en: "Users", ko: "사용자" }),
    title: pickAdminDashboardLocaleText(locale, { en: "User Operations Summary", ko: "사용자 운영 요약" }),
    defaultScopeTitle: pickAdminDashboardLocaleText(locale, {
      en: "Default scope: full-history recent users",
      ko: "기본 범위: 전체 히스토리 최근 사용자",
    }),
    defaultScopeBody: pickAdminDashboardLocaleText(locale, {
      en: "This screen opens with the latest 100 users across full history. Use the filters below only when you need to narrow to a specific user, request, or correlation trace.",
      ko: "이 화면은 전체 히스토리 기준 최신 사용자 100건을 먼저 엽니다. 특정 사용자, 요청, 상관관계 추적으로 좁혀야 할 때만 아래 필터를 사용합니다.",
    }),
    activeRoles: pickAdminDashboardLocaleText(locale, { en: "active roles", ko: "활성 역할" }),
    internalId: pickAdminDashboardLocaleText(locale, { en: "Internal ID", ko: "내부 ID" }),
    userHash: pickAdminDashboardLocaleText(locale, { en: "User Hash", ko: "사용자 해시" }),
    request: pickAdminDashboardLocaleText(locale, { en: "Request", ko: "요청" }),
    correlation: pickAdminDashboardLocaleText(locale, { en: "Correlation", ko: "상관관계" }),
    lookupUser: pickAdminDashboardLocaleText(locale, { en: "Lookup user", ko: "사용자 조회" }),
    reset: pickAdminDashboardLocaleText(locale, { en: "Reset", ko: "초기화" }),
    loading: pickAdminDashboardLocaleText(locale, { en: "Loading user summary...", ko: "사용자 요약을 불러오는 중입니다..." }),
    deniedTitle: pickAdminDashboardLocaleText(locale, { en: "Users denied", ko: "사용자 조회 권한 없음" }),
    deniedBody: pickAdminDashboardLocaleText(locale, { en: "User summary read permission is missing.", ko: "사용자 요약 조회 권한이 없습니다." }),
    failedTitle: pickAdminDashboardLocaleText(locale, { en: "Users failed", ko: "사용자 조회 실패" }),
    emptyTitle: pickAdminDashboardLocaleText(locale, { en: "No user summary", ko: "사용자 요약 없음" }),
    emptyBodyNoLookup: pickAdminDashboardLocaleText(locale, { en: "Enter one of internalUserId, userIdHash, requestId, or correlationId to start a lookup.", ko: "내부 사용자 ID, 사용자 해시, 요청 ID, 상관관계 ID 중 하나를 입력해 조회를 시작합니다." }),
    emptyBodyPrefix: pickAdminDashboardLocaleText(locale, { en: "No results. lookup:", ko: "조회 결과가 없습니다. 조회 기준:" }),
    eyebrow: pickAdminDashboardLocaleText(locale, { en: "User Operations", ko: "사용자 운영" }),
    description: pickAdminDashboardLocaleText(locale, { en: "An operator diagnostic surface that checks latest failure, latest successful save, and risk summary before drilling into stored user data.", ko: "최근 실패, 최근 저장 성공, 위험 요약을 먼저 확인한 뒤 상세 저장 데이터로 내려가는 운영자용 사용자 진단 화면입니다." }),
    internal: pickAdminDashboardLocaleText(locale, { en: "internal", ko: "내부 ID" }),
    accountCue: pickAdminDashboardLocaleText(locale, { en: "Account cue", ko: "계정 단서" }),
    providerCue: pickAdminDashboardLocaleText(locale, { en: "Provider cue", ko: "로그인 공급자 단서" }),
    linkedProviders: pickAdminDashboardLocaleText(locale, { en: "Linked providers", ko: "연결된 공급자 수" }),
    latestDeviceInstall: pickAdminDashboardLocaleText(locale, { en: "Latest device/install", ko: "최근 기기/설치 ID" }),
    latestPlatform: pickAdminDashboardLocaleText(locale, { en: "Latest platform", ko: "최근 플랫폼" }),
    latestRuntimeSummary: pickAdminDashboardLocaleText(locale, { en: "Latest runtime summary", ko: "최근 런타임 요약" }),
    runtimeSourceStatus: pickAdminDashboardLocaleText(locale, { en: "Runtime source status", ko: "런타임 source 상태" }),
    lookupResult: pickAdminDashboardLocaleText(locale, { en: "Lookup result", ko: "조회 결과" }),
    recentUserSummary: pickAdminDashboardLocaleText(locale, { en: "Recent user summary", ko: "최근 사용자 요약" }),
    openDetail: pickAdminDashboardLocaleText(locale, { en: "Open detail", ko: "상세 열기" }),
    viewSummary: pickAdminDashboardLocaleText(locale, { en: "View summary", ko: "요약 보기" }),
    metadata: pickAdminDashboardLocaleText(locale, { en: "Metadata", ko: "조회 메타데이터" }),
    created: pickAdminDashboardLocaleText(locale, { en: "Created", ko: "생성 시각" }),
    lastLogin: pickAdminDashboardLocaleText(locale, { en: "Last login", ko: "최근 로그인" }),
    terms: pickAdminDashboardLocaleText(locale, { en: "Terms", ko: "약관" }),
    deletion: pickAdminDashboardLocaleText(locale, { en: "Deletion", ko: "삭제" }),
    topRisk: pickAdminDashboardLocaleText(locale, { en: "Top risk", ko: "주요 위험" }),
    release: pickAdminDashboardLocaleText(locale, { en: "Release", ko: "릴리스" }),
    app: pickAdminDashboardLocaleText(locale, { en: "App", ko: "앱" }),
    generated: pickAdminDashboardLocaleText(locale, { en: "Generated", ko: "생성 시각" }),
    scenarioTimeline: pickAdminDashboardLocaleText(locale, { en: "Scenario Timeline", ko: "시나리오 타임라인" }),
    scenarioTitle: pickAdminDashboardLocaleText(locale, { en: "Latest success, failure, and footprint by scenario", ko: "시나리오별 최근 성공, 실패, 데이터 규모" }),
    scenarioDesc: pickAdminDashboardLocaleText(locale, { en: "Pet, daily, health, receipt, file, and account deletion flows are reordered by latest activity. If a failure trace exists, failed-flow is the first entry point. Otherwise compare footprint and status in detail.", ko: "반려동물, 일상, 건강, 영수증, 파일, 계정 삭제 흐름을 각각 최근 활동 기준으로 다시 정렬했습니다. 실패 추적이 있으면 오류 흐름이 첫 진입점이고, 없으면 상세 화면에서 현재 데이터 규모와 상태를 비교합니다." }),
    currentEvidenceTitle: pickAdminDashboardLocaleText(locale, { en: "Current evidence", ko: "현재 증거" }),
    currentEvidenceDesc: pickAdminDashboardLocaleText(locale, { en: "Read current risk, latest failed trace, and latest successful save together before drilling into the full user detail.", ko: "전체 사용자 상세로 내려가기 전에 현재 위험, 최신 실패 추적, 최신 저장 성공을 함께 읽습니다." }),
    footprint: pickAdminDashboardLocaleText(locale, { en: "Footprint", ko: "데이터 규모" }),
    pendingRisk: pickAdminDashboardLocaleText(locale, { en: "Pending risk", ko: "대기 위험" }),
    latestFailure: pickAdminDashboardLocaleText(locale, { en: "Latest failure", ko: "최근 실패" }),
    latestSuccess: pickAdminDashboardLocaleText(locale, { en: "Latest success", ko: "최근 성공" }),
    record: pickAdminDashboardLocaleText(locale, { en: "record", ko: "기록" }),
    receipt: pickAdminDashboardLocaleText(locale, { en: "receipt", ko: "영수증" }),
    riskSummary: pickAdminDashboardLocaleText(locale, { en: "Risk Summary", ko: "위험 요약" }),
    operatorView: pickAdminDashboardLocaleText(locale, { en: "Operator view", ko: "운영자 보기" }),
    risk: pickAdminDashboardLocaleText(locale, { en: "Risk", ko: "위험" }),
    signals: pickAdminDashboardLocaleText(locale, { en: "Signals", ko: "신호" }),
    action: pickAdminDashboardLocaleText(locale, { en: "Action", ko: "동작" }),
    openUserDataDetail: pickAdminDashboardLocaleText(locale, { en: "Open user data detail", ko: "사용자 데이터 상세 열기" }),
    evidence: pickAdminDashboardLocaleText(locale, { en: "evidence", ko: "근거" }),
    noImmediateRiskSignals: pickAdminDashboardLocaleText(locale, { en: "No risk signals require immediate intervention.", ko: "즉시 개입이 필요한 위험 신호가 없습니다." }),
    latestEvents: pickAdminDashboardLocaleText(locale, { en: "Latest Events", ko: "최근 이벤트" }),
    failure: pickAdminDashboardLocaleText(locale, { en: "Failure", ko: "실패" }),
    feature: pickAdminDashboardLocaleText(locale, { en: "Feature", ko: "기능" }),
    useCase: pickAdminDashboardLocaleText(locale, { en: "Use Case", ko: "유스케이스" }),
    error: pickAdminDashboardLocaleText(locale, { en: "Error", ko: "오류" }),
    occurred: pickAdminDashboardLocaleText(locale, { en: "Occurred", ko: "발생 시각" }),
    drilldown: pickAdminDashboardLocaleText(locale, { en: "Drilldown", ko: "연결 조사" }),
    openFailedFlow: pickAdminDashboardLocaleText(locale, { en: "Open failed flow", ko: "오류 흐름 열기" }),
    successfulSave: pickAdminDashboardLocaleText(locale, { en: "Successful save", ko: "저장 성공" }),
    event: pickAdminDashboardLocaleText(locale, { en: "Event", ko: "이벤트" }),
    meta: pickAdminDashboardLocaleText(locale, { en: "Meta", ko: "조회 메타" }),
    freshness: pickAdminDashboardLocaleText(locale, { en: "Freshness", ko: "신선도" }),
    recentFailures: pickAdminDashboardLocaleText(locale, { en: "Recent Failures", ko: "최근 실패" }),
    recentSaves: pickAdminDashboardLocaleText(locale, { en: "Recent Saves", ko: "최근 저장" }),
    reqShort: pickAdminDashboardLocaleText(locale, { en: "req", ko: "요청" }),
    corrShort: pickAdminDashboardLocaleText(locale, { en: "corr", ko: "상관관계" }),
    open: pickAdminDashboardLocaleText(locale, { en: "Open", ko: "열기" }),
    noRecentFailures: pickAdminDashboardLocaleText(locale, { en: "No recent failed flows.", ko: "최근 실패 흐름이 없습니다." }),
    noRecentSaves: pickAdminDashboardLocaleText(locale, { en: "No recent successful save events.", ko: "최근 저장 성공 이벤트가 없습니다." }),
    recentUsersTitle: pickAdminDashboardLocaleText(locale, { en: "Recent users first", ko: "최근 사용자 먼저 보기" }),
    recentUsersBody: pickAdminDashboardLocaleText(locale, {
      en: "The first load shows the latest 100 users across full history. Use the filter above only when you need to narrow to a specific user or request trace.",
      ko: "첫 진입에서는 전체 히스토리 기준 최신 사용자 100건을 먼저 보여줍니다. 특정 사용자나 요청 추적으로 좁힐 때만 위 필터를 사용하세요.",
    }),
    recentUsersReadingOrderTitle: pickAdminDashboardLocaleText(locale, {
      en: "Read the recent-user baseline before narrowing the lookup",
      ko: "lookup을 좁히기 전에 recent-user baseline부터 읽습니다",
    }),
    recentUsersReadingOrderBody: pickAdminDashboardLocaleText(locale, {
      en: "Start from the baseline list row that best matches the account cue, latest activity, and latest failure or save evidence you are investigating, then narrow the lookup only when you need a stricter trace scope.",
      ko: "조사 중인 계정 단서, 최근 활동, 최근 실패 또는 저장 근거와 가장 맞는 baseline 목록 행에서 먼저 시작하고, 더 엄격한 추적 범위가 필요할 때만 lookup을 좁힙니다.",
    }),
    latestActivity: pickAdminDashboardLocaleText(locale, { en: "Latest activity", ko: "최근 활동" }),
    usersShown: pickAdminDashboardLocaleText(locale, { en: "users shown", ko: "명 표시" }),
    noRecentUsers: pickAdminDashboardLocaleText(locale, { en: "No users were found across the default full-history scope.", ko: "기본 전체 히스토리 범위에서 사용자 데이터가 없습니다." }),
  };

  function handleLookupSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const formData = new FormData();
    formData.set("internalUserId", lookupDraft.internalUserId);
    formData.set("userIdHash", lookupDraft.userIdHash);
    formData.set("requestId", lookupDraft.requestId);
    formData.set("correlationId", lookupDraft.correlationId);
    setSearchParams(createLookupSearchParams(formData, locale));
  }

  function renderMetricCard(label: string, value: string, accent?: string) {
    return (
      <article
        style={{
          ...cardStyle,
          padding: "18px",
          background: accent ?? "rgba(255, 255, 255, 0.82)",
        }}
      >
        <p
          style={{
            margin: 0,
            color: "#64748b",
            fontSize: "0.78rem",
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}
        >
          {label}
        </p>
        <p
          style={{
            margin: "10px 0 0",
            fontSize: "1.45rem",
            fontWeight: 700,
            letterSpacing: "-0.03em",
          }}
        >
          {value}
        </p>
      </article>
    );
  }

  const renderRecentUsersSection = () => {
    if (listState.status === "loading" || listState.status === "idle") {
      return (
        <section style={cardStyle}>
          <p style={{ margin: 0, color: "#9a3412", fontWeight: 700 }}>{copy.recentUsersTitle}</p>
          <p style={{ margin: "10px 0 0", color: "#475569" }}>{copy.loading}</p>
        </section>
      );
    }

    if (listState.status === "permission_denied") {
      return (
        <AdminErrorState
          title={copy.deniedTitle}
          body={copy.deniedBody}
          guidance={{ screen: "users_summary", slot: "shared_error_state_guidance" }}
          requestId={listState.error.requestId}
          tone="permission_denied"
        />
      );
    }

    if (listState.status === "error") {
      return (
        <AdminErrorState
          title={copy.failedTitle}
          body={listState.error.message}
          guidance={{ screen: "users_summary", slot: "shared_error_state_guidance" }}
          requestId={listState.error.requestId}
          tone="error"
        />
      );
    }

    if (listState.status === "empty") {
      return <AdminEmptyState title={copy.emptyTitle} body={copy.noRecentUsers} />;
    }

    return (
      <section style={{ ...cardStyle, display: "grid", gap: "16px" }}>
        <div style={{ display: "grid", gap: "6px" }}>
          <p style={{ margin: 0, color: "#9a3412", fontWeight: 700 }}>{copy.recentUsersTitle}</p>
          <h4 style={{ margin: 0, fontSize: "1.18rem", letterSpacing: "-0.03em" }}>{copy.recentUsersBody}</h4>
          <p style={{ margin: 0, color: "#64748b", fontSize: "0.9rem" }}>
            {listState.viewModel.totalCountLabel}
          </p>
        </div>
        <div style={{ display: "grid", gap: "8px" }}>
          <div style={{ display: "grid", gap: "6px" }}>
            <p
              style={{
                margin: 0,
                color: "#64748b",
                fontSize: "0.78rem",
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              {copy.recentUsersTitle}
            </p>
            <p style={{ margin: 0, fontSize: "1rem", fontWeight: 700, color: "#18212b" }}>
              {copy.recentUsersReadingOrderTitle}
            </p>
            <p style={{ margin: 0, color: "#475569", lineHeight: 1.6 }}>
              {copy.recentUsersReadingOrderBody}
            </p>
          </div>
          <AdminOperatorExplanationBlock
            screen="users_summary"
            slot="users_summary_table_reading_order"
          />
        </div>
        <div style={{ display: "grid", gap: "12px" }}>
          {listState.viewModel.items.map((item) => (
            <article
              key={item.internalUserId}
              style={{
                padding: "16px",
                borderRadius: "18px",
                border: "1px solid rgba(148, 163, 184, 0.24)",
                background: "rgba(248, 250, 252, 0.92)",
                display: "grid",
                gap: "10px",
              }}
            >
                <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
                <div style={{ display: "grid", gap: "4px" }}>
                  <strong style={{ fontSize: "1rem", color: "#18212b" }}>{item.internalUserId}</strong>
                  <span style={{ color: "#64748b", fontSize: "0.86rem" }}>{item.userIdHash}</span>
                  <span style={{ color: "#475569", fontSize: "0.85rem" }}>{item.accountIdentityLabel}</span>
                  <span style={{ color: "#64748b", fontSize: "0.82rem" }}>{item.providerIdentityLabel}</span>
                  <span style={{ color: "#94a3b8", fontSize: "0.78rem" }}>{item.linkedProviderCountLabel}</span>
                  <span style={{ color: "#475569", fontSize: "0.82rem" }}>
                    {item.latestDeviceInstallIdLabel} / {item.latestPlatformLabel}
                  </span>
                  <span style={{ color: "#64748b", fontSize: "0.8rem" }}>{item.latestRuntimeSummaryLabel}</span>
                  {item.latestRuntimeCoverageLabel ? (
                    <span style={{ color: "#b45309", fontSize: "0.78rem", fontWeight: 600 }}>
                      {item.latestRuntimeCoverageLabel}
                    </span>
                  ) : null}
                </div>
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  {item.latestFailureDrilldownTo ? (
                    <AdminLocalizedLink
                      to={item.latestFailureDrilldownTo}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: "38px",
                        padding: "0 14px",
                        borderRadius: "999px",
                        background: "rgba(220, 38, 38, 0.1)",
                        color: "#b91c1c",
                        fontWeight: 700,
                        textDecoration: "none",
                      }}
                    >
                      {copy.openFailedFlow}
                    </AdminLocalizedLink>
                  ) : null}
                  <button
                    onClick={() => setSelectedBaselineInternalUserId(item.internalUserId)}
                    style={{
                      minHeight: "38px",
                      padding: "0 14px",
                      borderRadius: "999px",
                      border: "1px solid rgba(15, 118, 110, 0.28)",
                      background:
                        !hasExplicitLookup && selectedSummaryInternalUserId === item.internalUserId
                          ? "rgba(15, 118, 110, 0.12)"
                          : "#fff",
                      color: "#0f766e",
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                    type="button"
                  >
                    {copy.viewSummary}
                  </button>
                  <AdminLocalizedLink
                    to={buildDetailRouteWithMediaSelection(item.detailRoute, searchParams, locale)}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: "38px",
                      padding: "0 14px",
                      borderRadius: "999px",
                      background: "#0f766e",
                      color: "#fff",
                      fontWeight: 700,
                      textDecoration: "none",
                    }}
                  >
                    {copy.openDetail}
                  </AdminLocalizedLink>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "10px" }}>
                {renderMetricCard(copy.latestActivity, item.latestActivityAtLabel, "rgba(255,255,255,0.9)")}
                {renderMetricCard(copy.release, item.latestReleaseLabel, "rgba(255,255,255,0.9)")}
              </div>
              <div style={{ display: "grid", gap: "4px", color: "#334155", fontSize: "0.92rem" }}>
                <span>{item.countsLabel}</span>
                <span>{item.riskLabel}</span>
                {item.topRiskLabel ? <span>{copy.topRisk}: {item.topRiskLabel}</span> : null}
                <span>{copy.deletion}: {item.accountDeletionStatusLabel}</span>
                {item.latestFailureLabel ? <span>{copy.latestFailure}: {item.latestFailureLabel}</span> : null}
                {item.latestFailureContextLabels.length > 0 ? (
                  <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                    {item.latestFailureContextLabels.map((label) => (
                      <span
                        key={`${item.internalUserId}:${label}`}
                        style={{
                          padding: "4px 10px",
                          borderRadius: "999px",
                          background: "rgba(191, 219, 254, 0.42)",
                          color: "#1d4ed8",
                          fontSize: "0.8rem",
                          fontWeight: 600,
                        }}
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                ) : null}
                {item.latestSuccessLabel ? <span>{copy.latestSuccess}: {item.latestSuccessLabel}</span> : null}
              </div>
            </article>
          ))}
        </div>
      </section>
    );
  };

  return (
    <section style={{ display: "grid", gap: "18px" }}>
      <header style={cardStyle}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          <div>
            <p style={{ margin: 0, color: "#9a3412", fontWeight: 700 }}>{copy.section}</p>
            <h3 style={{ margin: "8px 0 0", fontSize: "1.5rem", letterSpacing: "-0.03em" }}>
              {copy.title}
            </h3>
          </div>
          <p style={{ margin: 0, color: "#64748b", fontSize: "0.9rem" }}>
            {copy.activeRoles}: {props.dependencies.adminRoles.join(", ")}
          </p>
        </div>
      </header>

      <section style={{ ...cardStyle, display: "grid", gap: "8px" }}>
        <p style={{ margin: 0, color: "#9a3412", fontWeight: 700 }}>{copy.defaultScopeTitle}</p>
        <p style={{ margin: 0, color: "#475569", lineHeight: 1.6 }}>{copy.defaultScopeBody}</p>
      </section>

      {renderRecentUsersSection()}

      <form onSubmit={handleLookupSubmit}>
        <AdminFilterBar
          items={[
            {
              id: "internalUserId",
              label: copy.internalId,
              control: (
                <input
                  id="internalUserId"
                  name="internalUserId"
                  onChange={(event) =>
                    setLookupDraft((current) => ({ ...current, internalUserId: event.target.value }))
                  }
                  style={baseControlStyle()}
                  value={lookupDraft.internalUserId}
                />
              ),
            },
            {
              id: "userIdHash",
              label: copy.userHash,
              control: (
                <input
                  id="userIdHash"
                  name="userIdHash"
                  onChange={(event) =>
                    setLookupDraft((current) => ({ ...current, userIdHash: event.target.value }))
                  }
                  style={baseControlStyle()}
                  value={lookupDraft.userIdHash}
                />
              ),
            },
            {
              id: "requestId",
              label: copy.request,
              control: (
                <input
                  id="requestId"
                  name="requestId"
                  onChange={(event) =>
                    setLookupDraft((current) => ({ ...current, requestId: event.target.value }))
                  }
                  style={baseControlStyle()}
                  value={lookupDraft.requestId}
                />
              ),
            },
            {
              id: "correlationId",
              label: copy.correlation,
              control: (
                <input
                  id="correlationId"
                  name="correlationId"
                  onChange={(event) =>
                    setLookupDraft((current) => ({ ...current, correlationId: event.target.value }))
                  }
                  style={baseControlStyle()}
                  value={lookupDraft.correlationId}
                />
              ),
            },
          ]}
          actions={
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <button
                style={{
                  minHeight: "40px",
                  padding: "0 14px",
                  borderRadius: "999px",
                  border: "none",
                  background: "#0f766e",
                  color: "#fff",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
                type="submit"
              >
                {copy.lookupUser}
              </button>
              <AdminLocalizedLink
                to="/admin/users"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  minHeight: "40px",
                  padding: "0 14px",
                  borderRadius: "999px",
                  border: "1px solid rgba(148, 163, 184, 0.45)",
                  color: "#18212b",
                  textDecoration: "none",
                  fontWeight: 700,
                }}
              >
                {copy.reset}
              </AdminLocalizedLink>
            </div>
          }
        />
      </form>

      {hasActiveLookup && (state.status === "loading" || state.status === "idle") ? (
        <section style={cardStyle}>
          <p style={{ margin: 0, color: "#9a3412", fontWeight: 700 }}>{copy.section}</p>
          <p style={{ margin: "10px 0 0", color: "#475569" }}>{copy.loading}</p>
        </section>
      ) : null}

      {hasActiveLookup && state.status === "permission_denied" ? (
        <AdminErrorState
          title={copy.deniedTitle}
          body={copy.deniedBody}
          guidance={{
            screen: "users_summary",
            slot: "shared_error_state_guidance",
          }}
          requestId={state.error.requestId}
          tone="permission_denied"
        />
      ) : null}

      {hasActiveLookup && state.status === "error" ? (
        <AdminErrorState
          title={copy.failedTitle}
          body={state.error.message}
          guidance={{
            screen: "users_summary",
            slot: "shared_error_state_guidance",
          }}
          requestId={state.error.requestId}
          tone="error"
        />
      ) : null}

      {hasExplicitLookup && state.status === "empty" ? (
        <AdminEmptyState
          title={copy.emptyTitle}
          body={
            lookupSummary
              ? `${copy.emptyBodyPrefix} ${lookupSummary}`
              : copy.emptyBodyNoLookup
          }
          guidance={{
            screen: "users_summary",
            slot: "shared_empty_state_guidance",
          }}
        />
      ) : null}

      {hasActiveLookup && (state.status === "ready" || state.status === "partial" || state.status === "stale") ? (
        <>
          <section
            style={{
              ...cardStyle,
              display: "grid",
              gridTemplateColumns: "minmax(0, 1.35fr) minmax(280px, 0.85fr)",
              gap: "18px",
              flexWrap: "wrap",
            }}
          >
            <div>
              <p style={{ margin: 0, color: "#64748b", fontSize: "0.82rem", fontWeight: 700 }}>
                {copy.eyebrow}
              </p>
              <p style={{ margin: "8px 0 0", fontSize: "1.35rem", fontWeight: 700 }}>
                {state.viewModel.userIdHash}
              </p>
              <p style={{ margin: "10px 0 0", color: "#475569", lineHeight: 1.6 }}>
                {copy.description}
              </p>
              <AdminOperatorExplanationBlock
                screen="users_summary"
                slot="users_summary_top_summary"
                style={{ marginTop: "14px" }}
              />
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "14px" }}>
                <AdminStatusBadge value={state.status} />
                <AdminStatusBadge
                  value={toAdminRiskBadgeValue(state.viewModel.riskSummary.highestLevel)}
                />
                <span style={{ color: "#64748b", fontSize: "0.88rem" }}>
                  {copy.internal}: {state.viewModel.internalUserId}
                </span>
                <span style={{ color: "#64748b", fontSize: "0.88rem" }}>
                  {copy.accountCue}: {state.viewModel.accountIdentityLabel}
                </span>
                <span style={{ color: "#64748b", fontSize: "0.88rem" }}>
                  {copy.providerCue}: {state.viewModel.providerIdentityLabel}
                </span>
                <span style={{ color: "#94a3b8", fontSize: "0.88rem" }}>
                  {copy.linkedProviders}: {state.viewModel.linkedProviderCountLabel}
                </span>
                <span style={{ color: "#64748b", fontSize: "0.88rem" }}>
                  {copy.latestDeviceInstall}: {state.viewModel.latestDeviceInstallIdLabel}
                </span>
                <span style={{ color: "#64748b", fontSize: "0.88rem" }}>
                  {copy.latestPlatform}: {state.viewModel.latestPlatformLabel}
                </span>
                <span style={{ color: "#64748b", fontSize: "0.88rem" }}>
                  {copy.latestRuntimeSummary}: {state.viewModel.latestRuntimeSummaryLabel}
                </span>
                {state.viewModel.latestRuntimeCoverageLabel ? (
                  <span style={{ color: "#b45309", fontSize: "0.88rem", fontWeight: 600 }}>
                    {copy.runtimeSourceStatus}: {state.viewModel.latestRuntimeCoverageLabel}
                  </span>
                ) : null}
              </div>
            </div>
            <div
              style={{
                display: "grid",
                gap: "10px",
                alignContent: "start",
                justifyItems: "start",
              }}
            >
                <AdminDetailPanel
                  title={isBaselineDetailMode ? copy.recentUserSummary : copy.lookupResult}
                  headerAction={
                  <AdminLocalizedLink style={{ color: "#0f766e", fontWeight: 700 }} to={detailRouteWithMediaSelection ?? state.viewModel.detailRoute}>
                    {copy.openDetail}
                  </AdminLocalizedLink>
                }
                sections={[
                  {
                    title: copy.metadata,
                    items: [
                      { key: "createdAt", label: copy.created, value: state.viewModel.createdAtLabel },
                      { key: "accountCue", label: copy.accountCue, value: state.viewModel.accountIdentityLabel },
                      { key: "providerCue", label: copy.providerCue, value: state.viewModel.providerIdentityLabel },
                      { key: "linkedProviders", label: copy.linkedProviders, value: state.viewModel.linkedProviderCountLabel },
                      { key: "latestDeviceInstall", label: copy.latestDeviceInstall, value: state.viewModel.latestDeviceInstallIdLabel },
                      { key: "latestPlatform", label: copy.latestPlatform, value: state.viewModel.latestPlatformLabel },
                      { key: "latestRuntimeSummary", label: copy.latestRuntimeSummary, value: state.viewModel.latestRuntimeSummaryLabel },
                      ...(state.viewModel.latestRuntimeCoverageLabel
                        ? [
                            {
                              key: "runtimeSourceStatus",
                              label: copy.runtimeSourceStatus,
                              value: state.viewModel.latestRuntimeCoverageLabel,
                            },
                          ]
                        : []),
                      { key: "lastLoginAt", label: copy.lastLogin, value: state.viewModel.lastLoginAtLabel },
                      { key: "termsConsentAt", label: copy.terms, value: state.viewModel.termsConsentAtLabel },
                      { key: "deletion", label: copy.deletion, value: state.viewModel.accountDeletionStatusLabel },
                      { key: "release", label: copy.release, value: state.viewModel.latestReleaseLabel },
                      { key: "appVersion", label: copy.app, value: state.viewModel.latestAppVersionLabel },
                      { key: "generatedAt", label: copy.generated, value: state.viewModel.generatedAtLabel },
                      { key: "requestId", label: copy.request, value: state.viewModel.requestId },
                    ],
                  },
                ]}
              />
            </div>
          </section>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "14px",
            }}
          >
            {state.viewModel.triageCards.map((card) => (
              <article
                key={card.key}
                style={{
                  ...cardStyle,
                  ...createTriageCardStyle(card.tone),
                  padding: "18px",
                  display: "grid",
                  gap: "12px",
                }}
              >
                <div style={{ display: "grid", gap: "6px" }}>
                  <p
                    style={{
                      margin: 0,
                      color: "#64748b",
                      fontSize: "0.78rem",
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                    }}
                  >
                    {card.title}
                  </p>
                  <p style={{ margin: 0, color: "#18212b", fontSize: "1.05rem", fontWeight: 700 }}>
                    {card.headline}
                  </p>
                  <p style={{ margin: 0, color: "#475569", lineHeight: 1.5, fontSize: "0.92rem" }}>
                    {card.supporting}
                  </p>
                </div>
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  {card.primaryActionLabel && card.primaryActionTo ? (
                    <AdminLocalizedLink
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        minHeight: "36px",
                        padding: "0 12px",
                        borderRadius: "999px",
                        background: "#0f766e",
                        color: "#fff",
                        fontWeight: 700,
                        textDecoration: "none",
                      }}
                      to={card.primaryActionTo}
                    >
                      {card.primaryActionLabel}
                    </AdminLocalizedLink>
                  ) : null}
                  {card.secondaryActionLabel && card.secondaryActionTo ? (
                    <AdminLocalizedLink
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        minHeight: "36px",
                        padding: "0 12px",
                        borderRadius: "999px",
                        border: "1px solid rgba(148, 163, 184, 0.35)",
                        color: "#18212b",
                        fontWeight: 700,
                        textDecoration: "none",
                      }}
                      to={card.secondaryActionTo}
                    >
                      {card.secondaryActionLabel}
                    </AdminLocalizedLink>
                  ) : null}
                </div>
              </article>
            ))}
          </div>

          <section style={{ ...cardStyle, display: "grid", gap: "16px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "start",
                gap: "14px",
                flexWrap: "wrap",
              }}
            >
              <div style={{ display: "grid", gap: "8px" }}>
                <p
                  style={{
                    margin: 0,
                    color: "#64748b",
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                  }}
                >
                  {copy.scenarioTimeline}
                </p>
                <h4 style={{ margin: 0, fontSize: "1.15rem", letterSpacing: "-0.02em" }}>
                  {copy.scenarioTitle}
                </h4>
              </div>
              <p style={{ margin: 0, color: "#475569", maxWidth: "720px", lineHeight: 1.6 }}>
                {copy.scenarioDesc}
              </p>
            </div>
            <AdminOperatorExplanationBlock
              screen="users_summary"
              slot="users_summary_scenario_timeline"
            />

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: "14px",
              }}
            >
              {state.viewModel.scenarioTimeline.map((item) => (
                <article
                  key={item.key}
                  style={{
                    ...cardStyle,
                    ...createTriageCardStyle(item.tone),
                    padding: "18px",
                    display: "grid",
                    gap: "12px",
                  }}
                >
                  <div style={{ display: "grid", gap: "6px" }}>
                    <p
                      style={{
                        margin: 0,
                        color: "#64748b",
                        fontSize: "0.78rem",
                        fontWeight: 700,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                      }}
                    >
                      {item.title}
                    </p>
                    <p style={{ margin: 0, color: "#18212b", fontSize: "1.05rem", fontWeight: 700 }}>
                      {item.headline}
                    </p>
                    <p style={{ margin: 0, color: "#475569", lineHeight: 1.5, fontSize: "0.92rem" }}>
                      {item.summary}
                    </p>
                  </div>

                  <div style={{ display: "grid", gap: "8px" }}>
                    <div
                      style={{
                        display: "grid",
                        gap: "6px",
                        padding: "12px 14px",
                        borderRadius: "16px",
                        background: "rgba(255, 255, 255, 0.7)",
                        border: "1px solid rgba(148, 163, 184, 0.18)",
                      }}
                    >
                      <p style={{ margin: 0, color: "#334155", fontSize: "0.9rem", fontWeight: 700 }}>
                        {item.latestActivityLabel}
                      </p>
                      <p style={{ margin: 0, color: "#475569", fontSize: "0.9rem" }}>
                        {copy.footprint}: {item.footprintLabel}
                      </p>
                      <p style={{ margin: 0, color: "#475569", fontSize: "0.9rem" }}>
                        {copy.pendingRisk}: {item.riskLabel}
                      </p>
                    </div>

                    {item.latestFailure ? (
                      <div
                        style={{
                          padding: "12px 14px",
                          borderRadius: "16px",
                          background: "rgba(254, 242, 242, 0.82)",
                          border: "1px solid rgba(239, 68, 68, 0.16)",
                          display: "grid",
                          gap: "4px",
                        }}
                      >
                        <p style={{ margin: 0, color: "#991b1b", fontSize: "0.82rem", fontWeight: 700 }}>
                          {copy.latestFailure}
                        </p>
                        <p style={{ margin: 0, color: "#18212b", fontWeight: 700 }}>
                          {item.latestFailure.errorCode}
                        </p>
                        <p style={{ margin: 0, color: "#475569", fontSize: "0.9rem" }}>
                          {item.latestFailure.feature} / {item.latestFailure.useCase} /{" "}
                          {item.latestFailure.occurredAtLabel}
                        </p>
                      </div>
                    ) : null}

                    {item.latestSuccessfulSave ? (
                      <div
                        style={{
                          padding: "12px 14px",
                          borderRadius: "16px",
                          background: "rgba(236, 253, 245, 0.82)",
                          border: "1px solid rgba(16, 185, 129, 0.16)",
                          display: "grid",
                          gap: "4px",
                        }}
                      >
                        <p style={{ margin: 0, color: "#047857", fontSize: "0.82rem", fontWeight: 700 }}>
                          {copy.latestSuccess}
                        </p>
                        <p style={{ margin: 0, color: "#18212b", fontWeight: 700 }}>
                          {item.latestSuccessfulSave.event}
                        </p>
                        <p style={{ margin: 0, color: "#475569", fontSize: "0.9rem" }}>
                          {item.latestSuccessfulSave.occurredAtLabel}
                          {item.latestSuccessfulSave.recordId
                            ? ` / ${copy.record} ${item.latestSuccessfulSave.recordId}`
                            : item.latestSuccessfulSave.receiptId
                              ? ` / ${copy.receipt} ${item.latestSuccessfulSave.receiptId}`
                              : ""}
                        </p>
                      </div>
                    ) : null}
                  </div>

                  <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                    <AdminLocalizedLink
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        minHeight: "36px",
                        padding: "0 12px",
                        borderRadius: "999px",
                        background: "#0f766e",
                        color: "#fff",
                        fontWeight: 700,
                        textDecoration: "none",
                      }}
                      to={item.primaryActionTo}
                    >
                      {item.primaryActionLabel}
                    </AdminLocalizedLink>
                    {item.secondaryActionLabel && item.secondaryActionTo ? (
                      <AdminLocalizedLink
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          minHeight: "36px",
                          padding: "0 12px",
                          borderRadius: "999px",
                          border: "1px solid rgba(148, 163, 184, 0.35)",
                          color: "#18212b",
                          fontWeight: 700,
                          textDecoration: "none",
                        }}
                        to={item.secondaryActionTo}
                      >
                        {item.secondaryActionLabel}
                      </AdminLocalizedLink>
                    ) : null}
                  </div>
                </article>
              ))}
            </div>
          </section>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
              gap: "14px",
            }}
          >
            {state.viewModel.counts.map((item) => (
              <div key={item.key}>{renderMetricCard(item.label, item.valueLabel)}</div>
            ))}
          </div>

          <div
            style={{
              display: "grid",
              gap: "12px",
            }}
          >
            <div style={{ display: "grid", gap: "8px" }}>
              <div style={{ display: "grid", gap: "6px" }}>
                <p
                  style={{
                    margin: 0,
                    color: "#64748b",
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                  }}
                >
                  {copy.currentEvidenceTitle}
                </p>
                <p style={{ margin: 0, color: "#475569", lineHeight: 1.6 }}>
                  {copy.currentEvidenceDesc}
                </p>
              </div>
              <AdminOperatorExplanationBlock
                screen="users_summary"
                slot="users_summary_current_evidence"
              />
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) minmax(300px, 0.95fr)",
                gap: "16px",
                alignItems: "start",
              }}
            >
            <AdminDetailPanel
              title={copy.riskSummary}
              sections={[
                {
                  title: copy.operatorView,
                  items: [
                    { key: "riskLevel", label: copy.risk, value: <AdminStatusBadge value={toAdminRiskBadgeValue(state.viewModel.riskSummary.highestLevel)} /> },
                    { key: "riskCount", label: copy.signals, value: String(state.viewModel.riskSummary.items.length) },
                    {
                      key: "detail",
                      label: copy.action,
                      value: (
                        <AdminLocalizedLink style={{ color: "#0f766e", fontWeight: 700 }} to={detailRouteWithMediaSelection ?? state.viewModel.detailRoute}>
                          {copy.openUserDataDetail}
                        </AdminLocalizedLink>
                      ),
                    },
                  ],
                },
              ]}
              footer={
                state.viewModel.riskSummary.items.length > 0 ? (
                  <div style={{ display: "grid", gap: "10px" }}>
                    {state.viewModel.riskSummary.items.map((item) => (
                      <div
                        key={`${item.code}:${item.evidence}`}
                        style={{
                          padding: "12px 14px",
                          borderRadius: "16px",
                          background: "rgba(248, 250, 252, 0.95)",
                          border: "1px solid rgba(148, 163, 184, 0.2)",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", flexWrap: "wrap" }}>
                          <strong style={{ color: "#18212b" }}>{item.label}</strong>
                          <AdminStatusBadge value={toAdminRiskBadgeValue(item.level)} />
                        </div>
                        <p style={{ margin: "8px 0 0", color: "#64748b", fontSize: "0.88rem" }}>
                          {copy.evidence}: {item.evidence}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ margin: 0, color: "#64748b" }}>{copy.noImmediateRiskSignals}</p>
                )
              }
            />

            <AdminDetailPanel
              title={copy.latestEvents}
              sections={[
                {
                  title: copy.failure,
                  items: [
                    {
                      key: "latestFailureFeature",
                      label: copy.feature,
                      value: state.viewModel.latestFailure?.feature ?? "-",
                    },
                    {
                      key: "latestFailureUseCase",
                      label: copy.useCase,
                      value: state.viewModel.latestFailure?.useCase ?? "-",
                    },
                    {
                      key: "latestFailureErrorCode",
                      label: copy.error,
                      value: state.viewModel.latestFailure?.errorCode ?? "-",
                    },
                    {
                      key: "latestFailureOccurredAt",
                      label: copy.occurred,
                      value: state.viewModel.latestFailure?.occurredAtLabel ?? "-",
                    },
                    {
                      key: "latestFailureDrilldown",
                      label: copy.drilldown,
                      value:
                        state.viewModel.latestFailure?.drilldownTo ? (
                          <AdminLocalizedLink to={state.viewModel.latestFailure.drilldownTo}>{copy.openFailedFlow}</AdminLocalizedLink>
                        ) : (
                          "-"
                        ),
                    },
                  ],
                },
                {
                  title: copy.successfulSave,
                  items: [
                    {
                      key: "latestSaveEvent",
                      label: copy.event,
                      value: state.viewModel.latestSuccessfulSave?.event ?? "-",
                    },
                    {
                      key: "latestSaveOccurredAt",
                      label: copy.occurred,
                      value: state.viewModel.latestSuccessfulSave?.occurredAtLabel ?? "-",
                    },
                    {
                      key: "latestSaveRecordId",
                      label: copy.record,
                      value: state.viewModel.latestSuccessfulSave?.recordId ?? "-",
                    },
                    {
                      key: "latestSaveReceiptId",
                      label: copy.receipt,
                      value: state.viewModel.latestSuccessfulSave?.receiptId ?? "-",
                    },
                  ],
                },
                {
                  title: copy.meta,
                  items: [
                    {
                      key: "freshness",
                      label: copy.freshness,
                      value: (
                        <AdminStatusBadge value={toAdminFreshnessBadgeValue(state.viewModel.freshness)} />
                      ),
                    },
                  ],
                },
              ]}
            />
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
              gap: "16px",
              alignItems: "start",
            }}
          >
            <AdminDetailPanel
              title={copy.recentFailures}
              footer={
                state.viewModel.recentFailures.length > 0 ? (
                  <div style={{ display: "grid", gap: "10px" }}>
                    {state.viewModel.recentFailures.map((failure) => (
                      <div
                        key={failure.id}
                        style={{
                          padding: "12px 14px",
                          borderRadius: "16px",
                          background: "rgba(255, 247, 237, 0.72)",
                          border: "1px solid rgba(251, 146, 60, 0.18)",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", flexWrap: "wrap" }}>
                          <strong>{failure.errorCode}</strong>
                          <span style={{ color: "#64748b", fontSize: "0.86rem" }}>{failure.occurredAtLabel}</span>
                        </div>
                        <p style={{ margin: "8px 0 0", color: "#18212b", fontWeight: 600 }}>
                          {failure.feature} / {failure.useCase}
                        </p>
                        {failure.contextLabels.length > 0 ? (
                          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "8px" }}>
                            {failure.contextLabels.map((label) => (
                              <span
                                key={label}
                                style={{
                                  borderRadius: "999px",
                                  background: "rgba(255, 255, 255, 0.88)",
                                  border: "1px solid rgba(251, 146, 60, 0.28)",
                                  color: "#9a3412",
                                  padding: "4px 10px",
                                  fontSize: "0.82rem",
                                  fontWeight: 600,
                                }}
                              >
                                {label}
                              </span>
                            ))}
                          </div>
                        ) : null}
                        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "8px" }}>
                          {failure.requestId ? <span style={{ color: "#64748b", fontSize: "0.86rem" }}>{copy.reqShort}: {failure.requestId}</span> : null}
                          {failure.correlationId ? <span style={{ color: "#64748b", fontSize: "0.86rem" }}>{copy.corrShort}: {failure.correlationId}</span> : null}
                          {failure.drilldownTo ? <AdminLocalizedLink to={failure.drilldownTo}>{copy.openFailedFlow}</AdminLocalizedLink> : null}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ margin: 0, color: "#64748b" }}>{copy.noRecentFailures}</p>
                )
              }
              sections={[]}
            />

            <AdminDetailPanel
              title={copy.recentSaves}
              footer={
                state.viewModel.recentSuccessfulSaves.length > 0 ? (
                  <div style={{ display: "grid", gap: "10px" }}>
                    {state.viewModel.recentSuccessfulSaves.map((save) => (
                      <div
                        key={save.id}
                        style={{
                          padding: "12px 14px",
                          borderRadius: "16px",
                          background: "rgba(240, 253, 250, 0.8)",
                          border: "1px solid rgba(20, 184, 166, 0.16)",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", flexWrap: "wrap" }}>
                          <strong>{save.event}</strong>
                          <span style={{ color: "#64748b", fontSize: "0.86rem" }}>{save.occurredAtLabel}</span>
                        </div>
                        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "8px" }}>
                          {save.recordId ? <span style={{ color: "#64748b", fontSize: "0.86rem" }}>{copy.record}: {save.recordId}</span> : null}
                          {save.receiptId ? <span style={{ color: "#64748b", fontSize: "0.86rem" }}>{copy.receipt}: {save.receiptId}</span> : null}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ margin: 0, color: "#64748b" }}>{copy.noRecentSaves}</p>
                )
              }
              sections={[]}
            />
          </div>
        </>
      ) : null}
    </section>
  );
}
